import { Fragment, useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    colors,
    DeleteOutlineIcon,
    DocOutlineIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import Separator from "components/DesignComponents/Separator";
import Tag, { TAG_COLORS, TAG_SIZES } from "components/DesignComponents/Tag";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import DropzoneContext from "context/dropzone/dropzoneContext";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";

import { UploadedFileSingleProps } from "../types";

const UploadedFileSingle = ({
    isLoading,
    countCsv
}: UploadedFileSingleProps) => {
    const { gray200, gray700, textPlaceholder } = colors;

    const { file, lineCount, setFile } = useContext(DropzoneContext);

    const { t } = useTranslation();
    const { renderFormattedNumber } = useFormattedNumber();

    return (
        <div
            css={css({
                border: `1px solid ${gray200}`,
                borderRadius: "8px",
                padding: "16px"
            })}
        >
            <div
                css={css({
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                })}
            >
                <div
                    css={css({
                        display: "flex",
                        alignItems: "center"
                    })}
                >
                    <DocOutlineIcon
                        css={css({
                            alignSelf: "flex-start",
                            color: textPlaceholder,
                            marginRight: "8px"
                        })}
                    />

                    <span
                        css={css({
                            color: gray700,
                            fontSize: "14px",
                            lineHeight: "20px",
                            fontWeight: "500",
                            letterSpacing: "0.1px"
                        })}
                    >
                        {file?.name}
                    </span>
                </div>

                <TooltipGeneral
                    placement={TOOLTIP_PLACEMENT.Top}
                    title={t("Button##delete")}
                    disableTouchListener
                    disableHoverListener={isLoading}
                >
                    <span>
                        <Button
                            data-testid="delete-file-button"
                            color={BUTTON_COLORS.PrimarySubtle}
                            size={BUTTON_SIZES.Small}
                            variant={BUTTON_VARIANTS.IconOnly}
                            icon={<DeleteOutlineIcon />}
                            onClick={() => setFile(null)}
                            disabled={isLoading}
                            css={css({
                                alignSelf: "flex-start"
                            })}
                        />
                    </span>
                </TooltipGeneral>
            </div>

            {countCsv && (
                <Fragment>
                    <Separator
                        css={css({
                            margin: "16px 0"
                        })}
                    />

                    <div
                        css={css({
                            display: "flex",
                            alignItems: "Center"
                        })}
                    >
                        <Tag
                            data-testid="line-count-tag"
                            size={TAG_SIZES.Tiny}
                            color={TAG_COLORS.White}
                            title={renderFormattedNumber(lineCount)}
                        />

                        <span
                            css={css({
                                color: gray700,
                                fontSize: "14px",
                                lineHeight: "20px",
                                fontWeight: "500",
                                letterSpacing: "0.1px",
                                marginLeft: "8px"
                            })}
                        >
                            {t("Dropzone##rows detected")}
                        </span>
                    </div>
                </Fragment>
            )}
        </div>
    );
};

export default UploadedFileSingle;
